// Token-level comparison for whole-sentence answers.
//
// Typing a whole sentence and being told only "wrong" is the complaint this exists to
// answer: a single mistyped letter used to invalidate the entire attempt with no
// indication of where. compare() returns two token lists - what was typed and what was
// expected - each marking only what the other lacks, so the panel can show the two
// lines with just the differing tokens highlighted.
//
// Dependency-free on purpose. Sentences are short, so the O(n*m) LCS table costs
// nothing measurable and pulling in jsdiff would add more bundle than it saves.
//
// IMPORTANT: the fold passed as `normalizeToken` must be character-wise (lowercasing,
// accent replacement), because sameText() applies it to a whole string while compare()
// applies it per token. If those two disagree, the panel will contradict the grade -
// the one failure mode this feature cannot have.

// Shared with PlayCollectionV5, which uses it for the masked sentence skeleton and to
// derive the per-sentence punctuation buttons. Keeps the /g flag: every use is
// String#match, which ignores lastIndex, so it is not stateful here.
export const PUNCTUATION_REGEX = /[!-/:-@[-`{-~¡-©«-¬®-±´¶-¸»¿×÷˂-˅˒-˟˥-˫˭˯-˿͵;΄-΅·϶҂՚-՟։-֊־׀׃׆׳-״؆-؏؛؞-؟٪-٭۔۩۽-۾܀-܍߶-߹।-॥॰৲-৳৺૱୰௳-௺౿ೱ-ೲ൹෴฿๏๚-๛༁-༗༚-༟༴༶༸༺-༽྅྾-࿅࿇-࿌࿎-࿔၊-၏႞-႟჻፠-፨᎐-᎙᙭-᙮᚛-᚜᛫-᛭᜵-᜶។-៖៘-៛᠀-᠊᥀᥄-᥅᧞-᧿᨞-᨟᭚-᭪᭴-᭼᰻-᰿᱾-᱿᾽᾿-῁῍-῏῝-῟῭-`´-῾\u2000-\u206e⁺-⁾₊-₎₠-₵℀-℁℃-℆℈-℉℔№-℘℞-℣℥℧℩℮℺-℻⅀-⅄⅊-⅍⅏←-⏧␀-␦⑀-⑊⒜-ⓩ─-⚝⚠-⚼⛀-⛃✁-✄✆-✉✌-✧✩-❋❍❏-❒❖❘-❞❡-❵➔➘-➯➱-➾⟀-⟊⟌⟐-⭌⭐-⭔⳥-⳪⳹-⳼⳾-⳿⸀-\u2e7e⺀-⺙⺛-⻳⼀-⿕⿰-⿻\u3000-〿゛-゜゠・㆐-㆑㆖-㆟㇀-㇣㈀-㈞㈪-㉃㉐㉠-㉿㊊-㊰㋀-㋾㌀-㏿䷀-䷿꒐-꓆꘍-꘏꙳꙾꜀-꜖꜠-꜡꞉-꞊꠨-꠫꡴-꡷꣎-꣏꤮-꤯꥟꩜-꩟﬩﴾-﴿﷼-﷽︐-︙︰-﹒﹔-﹦﹨-﹫！-／：-＠［-｀｛-･￠-￦￨-￮￼-�]|\ud800[\udd00-\udd02\udd37-\udd3f\udd79-\udd89\udd90-\udd9b\uddd0-\uddfc\udf9f\udfd0]|\ud802[\udd1f\udd3f\ude50-\ude58]|\ud809[\udc00-\udc7e]|\ud834[\udc00-\udcf5\udd00-\udd26\udd29-\udd64\udd6a-\udd6c\udd83-\udd84\udd8c-\udda9\uddae-\udddd\ude00-\ude41\ude45\udf00-\udf56]|\ud835[\udec1\udedb\udefb\udf15\udf35\udf4f\udf6f\udf89\udfa9\udfc3]|\ud83c[\udc00-\udc2b\udc30-\udc93]/g;

// Collapsing runs of whitespace and trimming are both bug fixes, not cosmetics: without
// them a double space mid-sentence or one trailing space makes a perfectly typed
// sentence impossible to complete. JS \s also covers NBSP (French puts U+00A0 before
// closing guillemets and question marks) and the ideographic space U+3000, so a typed
// plain space matches a target NBSP.
export function normalize(text) {
  return String(text == null ? "" : text).replace(/\s+/g, " ").trim();
}

// Whitespace as well as punctuation, and interleaved: French puts a space before ?, !, ;
// and closing guillemets, so stripping only the "?" off "Comment allez-vous ?" leaves a
// trailing space that nothing else removes - normalize() already ran and trimmed before
// the punctuation was exposed. Someone typing "Comment allez-vous?" would then be graded
// wrong for a perfectly correct answer, because the two differ by that one space.
export function stripTrailingPunctuationAndSpace(text) {
  let str = text;
  while(str.length && (str[str.length - 1].match(PUNCTUATION_REGEX) || /\s/.test(str[str.length - 1]))) {
    str = str.slice(0, -1);
  }
  return str;
}

// Whole-string equality - the grading check. compare() is only ever shown when this is
// false, so the two have to agree about what counts as correct.
export function sameText(a, b, { normalizeToken = null } = {}) {
  const fold = normalizeToken || ((token) => token);
  return stripTrailingPunctuationAndSpace(fold(normalize(a))) === stripTrailingPunctuationAndSpace(fold(normalize(b)));
}

// Granularity varies by language. Splitting Japanese or Chinese on whitespace yields a
// single token, so the whole sentence would come back marked wrong - precisely the
// problem this feature exists to fix. Those diff per character instead.
export function tokenize(text, spaceless = false) {
  const normalized = normalize(text);
  if(!normalized) {
    return [];
  }
  return spaceless ? Array.from(normalized) : normalized.split(" ");
}

// Tokens keep their original spelling for display; only these folded copies are
// compared. Trailing punctuation is dropped from the last token alone, mirroring
// sameText - strip it everywhere and the panel would forgive a missing mid-sentence
// comma that the grade still counts wrong.
function comparisonTokens(tokens, normalizeToken) {
  const fold = normalizeToken || ((token) => token);

  return tokens.map((token, i) => {
    const folded = fold(token);
    if(i !== tokens.length - 1) {
      return folded;
    }
    const stripped = stripTrailingPunctuationAndSpace(folded);
    // Falling back to the unstripped token matters: "" would compare equal to every
    // other token that stripped down to nothing.
    return stripped || folded;
  });
}

// Filled from the end so the walk below can move forward greedily.
function lcsTable(a, b) {
  const table = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

  for(let i = a.length - 1; i >= 0; i--) {
    for(let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j]
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  return table;
}

// A flat, in-order list of index pairs:
//   equal   - present in both
//   missing - in the expected sentence, absent from the attempt
//   extra   - in the attempt, not in the expected sentence
export function diffTokens(expected, actual) {
  const table = lcsTable(expected, actual);
  const ops = [];
  let i = 0;
  let j = 0;

  while(i < expected.length && j < actual.length) {
    if(expected[i] === actual[j]) {
      ops.push({ op: "equal", expectedIndex: i, actualIndex: j });
      i++;
      j++;
    }
    // A tie means both choices yield an equally short diff, but they mark different
    // tokens. Preferring `extra` - unless the expected token is about to match, in
    // which case `missing` gets us there sooner - keeps a transposition from
    // implicating the word that sat still between the two that swapped. Compare
    // "Ich habe das nicht gesehen." typed as "Das habe ich nicht gesehen.": the naive
    // tie-break marks "habe", which was never wrong.
    else if(table[i + 1][j] === table[i][j + 1]
      ? expected[i] === actual[j + 1]
      : table[i + 1][j] > table[i][j + 1]) {
      ops.push({ op: "missing", expectedIndex: i, actualIndex: null });
      i++;
    }
    else {
      ops.push({ op: "extra", expectedIndex: null, actualIndex: j });
      j++;
    }
  }

  while(i < expected.length) {
    ops.push({ op: "missing", expectedIndex: i, actualIndex: null });
    i++;
  }

  while(j < actual.length) {
    ops.push({ op: "extra", expectedIndex: null, actualIndex: j });
    j++;
  }

  return ops;
}

// The only function the component calls. Deciding which ops belong on which line lives
// here rather than in the component so there is one place to look.
//
// `lastTokenInProgress` says the attempt's final token is a word still being typed, not
// a finished one - true whenever the live input does not end in whitespace. See below.
export function compare(expectedText, actualText, { lastTokenInProgress = false, normalizeToken = null, spaceless = false } = {}) {
  const expectedTokens = tokenize(expectedText, spaceless);
  const actualTokens = tokenize(actualText, spaceless);
  const ops = diffTokens(
    comparisonTokens(expectedTokens, normalizeToken),
    comparisonTokens(actualTokens, normalizeToken)
  );

  // The attempt line shows what was typed and marks only the extra tokens; the expected
  // line shows what was wanted and marks only the missing ones. Each line marks exactly
  // what the other lacks.
  const attempt = ops
    .filter((o) => o.op !== "missing")
    .map((o) => ({ op: o.op, token: actualTokens[o.actualIndex], wrong: o.op === "extra" }));
  const expected = ops
    .filter((o) => o.op !== "extra")
    .map((o) => ({ op: o.op, token: expectedTokens[o.expectedIndex], wrong: o.op === "missing" }));

  return {
    spaceless,
    identical: ops.every((o) => o.op === "equal"),
    attempt: softenInProgressToken(attempt, expected, {
      // Per-character diffing has no notion of a partial token: one character is either
      // right or wrong, so there is nothing to soften.
      enabled: lastTokenInProgress && !spaceless,
      normalizeToken
    }),
    expected
  };
}

// While typing, the attempt's final token is usually a word in progress rather than a
// mistake. Striking through "co" when the answer is "comunes." accuses the reader of an
// error they are three keystrokes from not making, which is the wrong direction for a
// panel whose whole job is to be precise about what went wrong.
//
// Softened on the attempt line only. The expected line still marks the word as missing,
// which is true - it is not typed yet - and is the useful half: it tells you what you
// are reaching for.
//
// Only the final token, and only when it is a prefix of something the diff called
// missing. Editing a word mid-sentence is not covered; that would need the caret
// position, and the final token is the overwhelmingly common case.
function softenInProgressToken(attempt, expected, { enabled, normalizeToken }) {
  if(!enabled || !attempt.length) {
    return attempt;
  }

  const last = attempt[attempt.length - 1];
  if(!last.wrong) {
    return attempt;
  }

  const fold = normalizeToken || ((token) => token);
  const typed = fold(last.token);
  const inProgress = expected.some((t) => t.wrong && fold(t.token).indexOf(typed) === 0);

  if(!inProgress) {
    return attempt;
  }

  return attempt
    .slice(0, -1)
    .concat([{ op: "in-progress", token: last.token, wrong: false }]);
}
